import React from 'react';
import { Grid, MenuItem, Typography } from '@material-ui/core';
import { FormCheckbox, FormSelect } from '@lib/common';
import { CorporateAccount } from '@lib/state';

export const DELEGATE_PLACEHOLDER = '__none__';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A delegate option returned by useCorporateAccountDelegates */
export interface DelegateOption {
  userId: string;
  name?: { first?: string | null; last?: string | null } | null;
}

export interface ConfigureStepProps {
  /** Total number of selected reservations (for the description text) */
  reservationCount: number;
  /** Available corporate accounts to choose from */
  corporateAccounts: CorporateAccount[];
  /** Available delegates for the currently selected corporate account */
  delegates: DelegateOption[];
  /** react-hook-form register function */
  register: (rules?: Record<string, unknown>) => React.Ref<HTMLInputElement>;
  /** Currently selected corporate account id (from form.watch) */
  watchedAccountId: string;
  /** Whether the selected account matches the reservations' existing account */
  isSameAccount: boolean;
  /** Currently watched value of the preserveExistingDelegate checkbox */
  preserveExistingDelegate: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Step 1 of the Bulk Change Billing dialog — select a corporate account
 * and delegate before previewing financial impact.
 *
 * This component renders inside a FormContext and uses FormSelect which
 * reads the form context internally. The `register` prop is passed through
 * for validation rules only.
 */
export const ConfigureStep: React.FC<ConfigureStepProps> = ({
  reservationCount,
  corporateAccounts,
  delegates,
  register,
  watchedAccountId,
  isSameAccount,
  preserveExistingDelegate,
}) => {
  // The delegate is required whenever moving to a different corporate account, since it serves
  // as the fallback used when an existing delegate is not valid for the newly-selected account.
  // It is also required when `preserveExistingDelegate` is checked, for the same reason: the
  // selected delegate is used as the fallback when an existing delegate cannot be preserved.
  const delegateRequired = !isSameAccount || preserveExistingDelegate;
  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {reservationCount} reservation{reservationCount !== 1 ? 's' : ''} selected. Choose the new
        corporate account and delegate, then click <b>Preview</b> to review the financial impact
        before confirming.
      </Typography>
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={12}>
          <FormSelect
            name="newCorporateAccountId"
            label="New Corporate Account *"
            defaultValue=""
            fullWidth
            inputRef={register({ required: 'Corporate account is required' })}
          >
            <MenuItem value="">Select a corporate account</MenuItem>
            {corporateAccounts.map(ca => (
              <MenuItem key={ca.id} value={ca.id}>
                {ca.name}
              </MenuItem>
            ))}
          </FormSelect>
        </Grid>
        {watchedAccountId && isSameAccount && (
          <Grid item xs={12}>
            <FormCheckbox
              name="preserveExistingDelegate"
              label="Preserve each reservation's existing delegate when valid"
              defaultChecked={false}
              fullWidth
            />
          </Grid>
        )}
        {watchedAccountId && (
          <Grid item xs={12}>
            <FormSelect
              name="newDelegateUserId"
              label={delegateRequired ? 'New Delegate *' : 'New Delegate'}
              defaultValue={DELEGATE_PLACEHOLDER}
              fullWidth
              inputRef={register(
                delegateRequired
                  ? {
                      validate: (v: string) =>
                        (v && v !== DELEGATE_PLACEHOLDER) || 'Delegate is required',
                    }
                  : {}
              )}
              helperText={
                preserveExistingDelegate
                  ? "Used when a reservation's existing delegate is not valid for the new corporate account."
                  : undefined
              }
            >
              <MenuItem value={DELEGATE_PLACEHOLDER}>
                {isSameAccount ? 'Keep existing delegate' : 'Select a delegate'}
              </MenuItem>
              {delegates.map(d => (
                <MenuItem key={d.userId} value={d.userId}>
                  {d.name?.first} {d.name?.last}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
        )}
      </Grid>
    </>
  );
};
